/**
 * 「计时面板」的**唯一判据与文案**（纯函数，无 React、无 DOM、无网络）。
 *
 * ## 这个文件为什么存在（2026-10-17 用户实测的 bug）
 *
 * 用户原话：
 *
 * > 孩子端这里点了暂停后，过一段时间想重新开始，就变成这样，**看不到那个计时的了**。
 *
 * 截图里那一项状态是「计时中」、按钮是「暂停 / 完成」，**但那个大的已用时间数字没了**。
 * 根因是「还有效」那一栏的行（`components/homework/OverdueRow.tsx` 的 `queue='open'`
 * 一支）**从来没渲染过计时面板** —— 计时面板当时是**两份**手抄的 JSX：
 * 「今天的作业」里一份（`StudentHomeworkPage` 的 `HomeworkItemRow`）、
 * 今日打卡页里一份（`StudentTodayPage` 的 `TaskRow`）。第三处（队列行）没有抄，
 * 于是孩子在那里**既看不到已用时间、也确认不了计时状态**。
 *
 * 所以"大号已用时间"这件事现在是：**判据在本文件（可测）+ 呈现只有一个组件**
 * （`apps/web/src/components/ui/CheckinTimerPanel.tsx`）。三条队列
 * （今天的作业 / 超时 / 还有效）与今日打卡页**共用**它，⛔ 不要再抄第三份。
 *
 * ## 三条不能改的语义
 *
 * 1. **服务端权威**：已用秒数只认服务端确认过的那个数（`actualSeconds` /
 *    队列里的 `recompute_checkin_seconds()`），本地只做**单调时钟推算**
 *    （见 `timer-clock.ts`）。这里不解析 `startedAt`、不跟 `Date.now()` 相减。
 * 2. **暂停不计时**：`paused` / `auto_paused` 的秒数**冻结**在服务端给的值上，
 *    只有 `running` 才允许加上本地走过的秒数。
 * 3. **显示单调不减**：同一行里已用时间只能往前走。
 *    `elapsedSeconds = max(服务端秒数, 本地推算秒数)` —— 见 `describeTimerPanel()`
 *    里那句注释（暂停很久后点「继续」时的第一帧、以及心跳乱序都会用到它）。
 */

import type { CheckinStatus } from './constants.js';

// ================================================================ 状态集合

/**
 * 「计时面板要显示、而且时长必须实时重算」的三个状态 —— **全项目唯一的一份**。
 *
 * 谁在用：
 *  - **前端**：`describeTimerPanel()` 据它决定要不要显示大号已用时间；
 *  - **服务端**：`listCheckinQueue()`（「超时」/「还有效」两条队列）与
 *    `loadHomeworkDay()`（某天的作业单）据它决定"哪些行的 `actual_seconds`
 *    必须用 `recompute_checkin_seconds()` 实时重算"，而不是读那一列的旧快照。
 *
 * ⚠️ 为什么要"实时重算"：`checkin.actual_seconds` 这一列**只在
 * finish / skip / 自动暂停时**才被写回（见 `checkins.service.ts` 的
 * `finishCheckin` / `skipCheckin` 与 `apps/worker/src/jobs.ts` 的
 * `autoPauseStaleCheckins`），**pause / resume / heartbeat 都不写它**。
 * 于是"孩子暂停了、准备待会儿继续"的那一行，库里那一列还是 `0` ——
 * 谁读它谁就会显示 `00:00`（2026-10-17 生产数据实测：某一项 recompute 出 358 秒，
 * 而 `actual_seconds` 仍是 0）。所以**这三个状态一律走 recompute，不读列**。
 *
 * 为什么不含 `pending` / `submitted` / `done` / `skipped` / `missed`：
 * 它们没有"正在累计的计时区间"，`actual_seconds` 已经是结算后的终值。
 */
export const LIVE_TIMER_STATUSES: readonly CheckinStatus[] = ['running', 'paused', 'auto_paused'];

/** 这一行的时长要不要实时重算 / 要不要显示计时面板（见 `LIVE_TIMER_STATUSES`）。 */
export function isLiveTimerStatus(status: string | null | undefined): boolean {
  if (!status) return false;
  return (LIVE_TIMER_STATUSES as readonly string[]).includes(status);
}

// ================================================================ 时钟格式

/**
 * 计时器显示用的时长：`'12:34'`（分:秒）或 `'1:02:03'`（时:分:秒）。
 *
 * 刻意不用本包的 `formatDuration`（那个给的是"19 分 23 秒"这种**句子里嵌的
 * 叙述式用时**）：计时器需要等宽数字、秒级跳动、位数稳定的格式。
 * 两者都保留秒，区别只在排版：一个给"读"，一个给"看着它走"。
 *
 * ⚠️ 这是**唯一一份**时钟格式：`apps/web/src/lib/utils.ts` 的 `formatClock()`
 * 转发到这里（别在两处各写一遍 `padStart`）。
 */
export function formatElapsedClock(seconds: number | null | undefined): string {
  const total = Number.isFinite(seconds) ? Math.max(0, Math.floor(seconds as number)) : 0;
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
}

// ================================================================ 自动暂停提醒

/**
 * "预计时长 × 2 且无操作 → 自动暂停"的**前端提示**（服务端才是真正的判定者，
 * 见 `apps/worker/src/jobs.ts` 的 `autoPauseStaleCheckins`）。
 *
 * 这里只决定"要不要提前提醒孩子"，不改变任何行为。
 */
export function shouldWarnAutoPause(
  elapsedSeconds: number,
  estimatedSeconds: number | null | undefined,
): boolean {
  if (!estimatedSeconds || estimatedSeconds <= 0) return false;
  return elapsedSeconds >= estimatedSeconds * 2;
}

// ================================================================ 面板视图模型

/** 面板文案（**逐字**，三个视图与今日打卡页完全一致）。 */
export const TIMER_PANEL_TEXT = {
  running: '正在计时',
  paused: '已暂停，点「继续」接着算',
  /** 心跳失败：本地继续按单调时钟走，服务端偏爱"少算不多算"。 */
  networkHint: '网络不太好，正在本地继续计时',
  /** 超过预计时长两倍（服务端随时可能自动暂停）。 */
  autoPauseHint: '⏰ 已超过预计时长两倍，服务器可能自动暂停',
} as const;

/** `describeTimerPanel()` 的入参（全部是纯值，`now` 不参与判定）。 */
export interface TimerPanelInput {
  /** `checkin.status`（或队列行的 `status`）。 */
  status: CheckinStatus | string;
  /**
   * **服务端确认过**的已用秒数。
   *
   * 三个来源，语义相同：作业单/今日清单的 `checkin.actualSeconds`、
   * 队列行的 `actualSeconds`（服务端已用 `recompute_checkin_seconds()` 实时算过）、
   * 以及心跳响应里的 `actualSeconds`。
   */
  serverSeconds: number | null | undefined;
  /**
   * 前端按**单调时钟**推算出的秒数（只有 `running` 有意义；其余状态传 `null`）。
   * 省略 = 不推算（家长端只读视图、或这一端没有在计时）。
   */
  tickingSeconds?: number | null;
  /** 界面上「预计 X」用的那个秒数；`null` = 没设过 → 不提示"超过两倍"。 */
  estimatedSeconds?: number | null;
  /** 最近一次心跳失败（只在 `running` 时有意义）。 */
  heartbeatFailed?: boolean;
}

/** `describeTimerPanel()` 的结果（组件只负责把它画出来，不再做判断）。 */
export interface TimerPanelView {
  /** 要不要渲染这一块（`false` = 这一行没有计时面板）。 */
  visible: boolean;
  isRunning: boolean;
  isPaused: boolean;
  /** 面板上那个大号数字的秒数（**已经**做过"只前进不后退"的守卫）。 */
  elapsedSeconds: number;
  /** 面板上那个大号数字的文本（`formatElapsedClock` 的结果）。 */
  clock: string;
  /** 状态说明：`正在计时` / `已暂停，点「继续」接着算`。 */
  note: string;
  /** 超过预计两倍（服务端可能自动暂停）。 */
  warnAutoPause: boolean;
  /** 两句附加提示（`null` = 不显示）。 */
  hints: { network: string | null; autoPause: string | null };
}

/**
 * 一行打卡的计时面板该长什么样 —— **判据全在这里，组件里不许再有第二份**。
 *
 * ## 为什么 `elapsedSeconds` 要取 `max(服务端, 本地推算)`
 *
 * 两个真实场景都靠它才会"不跳变"：
 *
 *  1. **暂停很久后点「继续」**：恢复的那一刻服务端给的 `actualSeconds` 是
 *     上一段的累计（生产实测 358 秒），而前端锚点是**刚重置**的 0。
 *     若直接显示本地推算值，孩子会看到 `05:58 → 00:00 → 00:01…` 的倒退，
 *     以为"时间被清零了"（这正是 2026-10-13 那个"每 30 秒归零"的形态）。
 *  2. **第一帧**（刷新页面 / 刚切到「还有效」标签）：锚点要等 effect 跑完才生效，
 *     那一帧本地推算必然是 0。取 max 之后第一帧就等于服务端值，
 *     顺带也消掉了"到点提示音"被误判成刚跨过阈值的那一声
 *     （见 `estimate-chime.ts` 里 `serverSeconds` 的注释）。
 *
 * ⚠️ 这与 `timer-clock.ts` 的 `anchorFromServerSeconds()`（锚点只前进）是**同一套
 * 语义的两层**：持久化的锚点由它守，这一帧的显示由这里守。别删任何一层。
 */
export function describeTimerPanel(input: TimerPanelInput): TimerPanelView {
  const status = input.status;
  const isRunning = status === 'running';
  const isPaused = status === 'paused' || status === 'auto_paused';
  const visible = isLiveTimerStatus(status);

  const serverSeconds = toSeconds(input.serverSeconds);
  const tickingSeconds = isRunning ? toSeconds(input.tickingSeconds) : 0;
  /** 只前进不后退（见上面 "为什么取 max"）。 */
  const elapsedSeconds = Math.max(serverSeconds, tickingSeconds);

  const warnAutoPause = isRunning && shouldWarnAutoPause(elapsedSeconds, input.estimatedSeconds);

  return {
    visible,
    isRunning,
    isPaused,
    elapsedSeconds,
    clock: formatElapsedClock(elapsedSeconds),
    note: isRunning ? TIMER_PANEL_TEXT.running : TIMER_PANEL_TEXT.paused,
    warnAutoPause,
    hints: {
      network: isRunning && input.heartbeatFailed === true ? TIMER_PANEL_TEXT.networkHint : null,
      autoPause: warnAutoPause ? TIMER_PANEL_TEXT.autoPauseHint : null,
    },
  };
}

/** 合法的秒数（负数 / NaN / Infinity → 0），与 `timer-clock.ts` 同一口径。 */
function toSeconds(value: number | null | undefined): number {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) return 0;
  return Math.floor(value);
}
